import { cn } from '@/lib/utils';
import { ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { clog } from '@chiroup/core/functions/clog';
import dayjs, { isDayjs } from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

dayjs.extend(utc);
dayjs.extend(tz);

type Props = {
  ts?: any;
  itemTz?: string;
  userTz?: string;
  errorValue?: ReactNode;
  className?: string;
  logWarnings?: boolean;
  noTimezoneIsError?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  title?: string;
  asDateOnly?: boolean;
  format?: string;
  dateString?: string | null;
  utcDateOnly?: boolean;
  link?: string;
  showTimezone?: boolean;
};

const componentName = 'TimestampDisplay';

export const TimestampDisplay: React.FC<Props> = ({
  ts,
  itemTz,
  userTz,
  className = '',
  errorValue = <cite className="text-gray-400">-na-</cite>,
  logWarnings = false,
  noTimezoneIsError = false,
  prefix = null,
  suffix = null,
  title = '',
  format = ChiroUpDayJsCommon.format.datetime,
  dateString = null,
  asDateOnly = false,
  utcDateOnly = false,
  link = '',
  showTimezone = true,
}) => {
  if (isDayjs(ts)) {
    ts = ts.valueOf();
  }

  if (utcDateOnly) {
    if (!ts) return errorValue;
    const ret = new Date(ts).toISOString().split('T')[0];

    if (format) {
      return <div>{dayjs(ret).format(format)}</div>;
    }

    return <div>{ret}</div>;
  }

  if (dateString) {
    let showDate = dateString;

    try {
      if (dateString.indexOf('/') > -1) {
        ts = new Date(dateString).getTime();
      } else if (dateString.indexOf('-') > -1) {
        // Use 'T' separator for ISO 8601 compliance - iOS Safari doesn't accept space separator
        ts = new Date(`${dateString}T00:00:00`).getTime();
      }

      showDate = dayjs(ts).format(ChiroUpDayJsCommon.format.date);
    } catch (e) {
      if (logWarnings) {
        clog(`${componentName}: Error parsing date string: ${dateString}`);
      }

      return errorValue;
    }

    return link ? (
      <Link
        to={link}
        className={cn(
          className,
          link ? 'cursor-pointer text-primary-600 hover:text-primary-500 hover:underline' : ''
        )}
        title={title}
      >
        {prefix}
        {showDate}
        {suffix}
      </Link>
    ) : (
      <div
        className={cn(
          className,
          link ? 'cursor-pointer text-primary-600 hover:text-primary-500 hover:underline' : ''
        )}
        title={title}
      >
        {prefix}
        {showDate}
        {suffix}
      </div>
    );
  }

  if (!ts) {
    if (logWarnings) clog(`${componentName}: No timestamp value.`);

    return errorValue;
  }

  if (!itemTz) {
    if (logWarnings) clog(`${componentName}: No item timezone.`);
    itemTz = ChiroUpDayJsCommon.defaultTimezone;
    if (noTimezoneIsError) return errorValue;
  }

  if (!userTz) {
    if (logWarnings) clog(`${componentName}: No user timezone.`);
    userTz = ChiroUpDayJsCommon.defaultTimezone;
    if (noTimezoneIsError) return errorValue;
  }

  if (asDateOnly) {
    return link ? (
      <Link
        to={link}
        className={cn(
          className,
          link ? 'cusor-pointer text-primary-600 hover:text-primary-500 hover:underline' : ''
        )}
        title={title}
      >
        {prefix}
        {ChiroUpDayJsCommon.display.dateWithTz(
          ts,
          itemTz,
          userTz,
          ChiroUpDayJsCommon.format.date,
          showTimezone
        )}
        {suffix}
      </Link>
    ) : (
      <div
        className={cn(
          className,
          link ? 'cusor-pointer text-primary-600 hover:text-primary-500 hover:underline' : ''
        )}
        title={title}
      >
        {prefix}
        {ChiroUpDayJsCommon.display.dateWithTz(
          ts,
          itemTz,
          userTz,
          ChiroUpDayJsCommon.format.date,
          showTimezone
        )}
        {suffix}
      </div>
    );
  }

  return link ? (
    <Link
      to={link}
      className={cn(
        className,
        link ? 'cusor-pointer text-primary-600 hover:text-primary-500 hover:underline' : ''
      )}
      title={title}
    >
      {prefix}
      {ChiroUpDayJsCommon.display.datetimeWithTz(ts, itemTz, userTz, format, showTimezone)}
      {suffix}
    </Link>
  ) : (
    <div
      className={cn(
        className,
        link ? 'cusor-pointer text-primary-600 hover:text-primary-500 hover:underline' : ''
      )}
      title={title}
    >
      {prefix}
      {ChiroUpDayJsCommon.display.datetimeWithTz(ts, itemTz, userTz, format, showTimezone)}
      {suffix}
    </div>
  );
};
